import React, { useEffect, useRef, type JSX } from 'react';

export interface Props {
  /** Optional heading override. Defaults to museum-visitor friendly copy. */
  title?: string;
  /** Optional supporting line under the heading. */
  message?: string;
  onRetry?: () => void;
  onHome?: () => void;
}

/**
 * Friendly full-screen fallback shown when a code can't be loaded (unknown or
 * deleted code, network loss, bad payload). Replaces the previous behaviour of
 * spinning forever on a permanent <Loading />. Copy is written for a first-time
 * museum visitor, not a developer.
 */
const ErrorScreen: React.FC<Props> = ({
  title = "We couldn't find that trail",
  message = 'Please double-check the code on your card or sign. If you have a signal, tap Try again.',
  onRetry,
  onHome,
}: Props): JSX.Element => {
  const containerRef = useRef<HTMLDivElement | null>(null);

  // Move keyboard focus onto the error so it's announced and reachable.
  useEffect(() => {
    containerRef.current?.focus();
  }, []);

  return (
    <div
      ref={containerRef}
      role="alert"
      aria-labelledby="error-screen-title"
      aria-describedby="error-screen-copy"
      tabIndex={-1}
      className="start-screen flex h-screen flex-col items-center justify-center gap-6 bg-cover bg-center bg-no-repeat outline-none"
    >
      <div className="bg-mt-surface flex w-72 flex-col gap-4 rounded-2xl p-6 text-center shadow-2xl">
        <h1
          id="error-screen-title"
          className="text-mt-ink-soft text-xl font-bold tracking-tight"
        >
          {title}
        </h1>
        <p id="error-screen-copy" className="text-mt-ink-muted text-sm">
          {message}
        </p>
        {onRetry && (
          <button
            type="button"
            onClick={onRetry}
            className="mt-btn bg-mt-primary text-mt-on-primary hover:bg-mt-primary-hover focus-visible:outline-mt-primary inline-flex items-center justify-center gap-x-2 rounded-md px-3.5 py-2.5 text-sm font-semibold shadow-xs focus-visible:outline-2 focus-visible:outline-offset-2"
          >
            Try again
          </button>
        )}
        {onHome && (
          <button
            type="button"
            onClick={onHome}
            className="text-mt-primary text-sm hover:underline"
          >
            Back home
          </button>
        )}
      </div>
    </div>
  );
};

export default ErrorScreen;
